import React, { useState } from 'react';
import Grid from '@material-ui/core/Grid';
import InputLabel from '@material-ui/core/InputLabel';
import TextField from '@material-ui/core/TextField';
import { MfsLanguage } from '../../interfaces/MfsLanguage';
import { Rater } from '../../interfaces/Rater';
import { useTranslation } from 'react-i18next';
import { RaterStyles } from '../../styles/rater';
import { Divider } from '@material-ui/core';
import { StringMap } from '../../../common/interfaces/stringMap';
import Autocomplete from '@material-ui/lab/Autocomplete';

interface AddRaterFormProps {
  rater: Rater;
  languages?: Array<MfsLanguage>;
  onInputChange(name: keyof Rater): (event?: React.ChangeEvent<{ value: string }>, value?: string) => void;
}

export interface RaterField {
  label: string;
  responseType: string;
  options: null | { label: string; id: string }[];
  sequence: number;
  mandatory: boolean;
  participantVisible: boolean;
}

const fieldNameKeyMap: StringMap = {
  2: 'firstName',
  3: 'lastName',
  4: 'email',
  5: 'pID',
  6: 'country',
  7: 'region',
  8: 'dept',
  9: 'level',
};

export const SearchRaterForm = ({ rater, onInputChange }: AddRaterFormProps): JSX.Element => {
  const { t } = useTranslation();
  const classes = RaterStyles();
  const fields = sessionStorage.getItem('searchFields');
  const [searchFields] = useState<RaterField[]>(JSON.parse(fields ? fields : '{"fields":[]}').fields);
  const visibleFields = searchFields.filter((filed) => filed.participantVisible === true);

  const getInputFields = (field: RaterField): JSX.Element => {
    switch (field.responseType) {
      case 'TEXT':
      case 'EMAIL':
        return (
          <TextField
            id={field.label}
            type="text"
            variant="outlined"
            fullWidth
            name={field.label}
            autoComplete="text"
            size="small"
            value={rater[fieldNameKeyMap[field.sequence]]}
            placeholder={`${t('Enter')} ${field.label}`}
            onChange={onInputChange(fieldNameKeyMap[field.sequence])}
            onBlur={onInputChange(fieldNameKeyMap[field.sequence])}
            inputProps={{
              'aria-label': field.label,
              maxLength: '100',
              className: classes.bgWhite,
            }}
          />
        );

      case 'LIST':
        return (
          <Autocomplete
            disablePortal
            className={classes.bgWhite}
            id={field.label}
            options={field.options ? field.options : []}
            getOptionLabel={(option: { label: string; id: string }): string => (option.label ? option.label : '')}
            onInputChange={(event: React.ChangeEvent<{}>, value: string): void => {
              const temp = field.options?.filter((option) => option.label === value);
              onInputChange(fieldNameKeyMap[field.sequence])(undefined, temp && temp.length > 0 ? temp[0].id : '-1');
            }}
            renderInput={(params): JSX.Element => (
              <TextField
                {...params}
                id={field.label}
                disabled={false}
                fullWidth
                placeholder={`${t('Enter')} ${field.label}`}
                onChange={onInputChange(fieldNameKeyMap[field.sequence])}
                size="small"
                variant="outlined"></TextField>
            )}
          />
        );

      default:
        return <></>;
    }
  };

  return (
    <Grid item xs={12}>
      <Grid container spacing={2}>
        <Divider orientation="horizontal" className={classes.searchDivider} />
        <Grid container spacing={2} className={classes.searchForm}>
          {visibleFields.map(
            (field: RaterField, index: number): JSX.Element => {
              if (field.participantVisible) {
                return (
                  <>
                    <Grid key={index} item xs={12} className={classes.searchFields}>
                      <Grid container spacing={1}>
                        <Grid item xs={12}>
                          <InputLabel htmlFor={field.label}>
                            <b>{field.label}</b>
                          </InputLabel>
                        </Grid>
                        <Grid item xs={12} translate="no">
                          {getInputFields(field)}
                        </Grid>
                      </Grid>
                    </Grid>

                    {[1, 3, 5, 7].includes(index) && visibleFields.length - index > 1 && (
                      <Divider orientation="horizontal" className={`${classes.searchDivider}`} />
                    )}
                  </>
                );
              } else {
                return <></>;
              }
            },
          )}
        </Grid>
        <Divider orientation="horizontal" className={classes.searchDivider} />
      </Grid>
    </Grid>
  );
};
